import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { formatCount } from '@/utils/number';
import { resolveApiErrorMessage } from '@/utils/errors';
import { pcmpProjectDeleteCheckQueryOptions } from '../api/pcmpProjects.api';
import { useDeletePcmpProject } from '../hooks/useDeletePcmpProject';
import { useSetPcmpProjectStatus } from '../hooks/useSetPcmpProjectStatus';

export interface PcmpProjectDeleteTarget {
  id: number;
  displayName: string;
  isActive: boolean;
}

interface PcmpProjectDeleteConfirmationProps {
  project: PcmpProjectDeleteTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/** Legacy DeleteInfo + Delete: checks first, and offers Deactivate when blocked. */
export function PcmpProjectDeleteConfirmation({
  project,
  open,
  onOpenChange,
  onDeleted,
}: PcmpProjectDeleteConfirmationProps) {
  const projectId = project?.id ?? 0;
  const check = useQuery({
    ...pcmpProjectDeleteCheckQueryOptions(projectId),
    enabled: open && project !== null,
  });

  const { mutate: deleteProject, isPending: isDeleting } = useDeletePcmpProject(() => {
    onOpenChange(false);
    onDeleted?.();
  });
  const { mutate: setStatus, isPending: isDeactivating } = useSetPcmpProjectStatus();

  if (!project) return null;

  const close = () => onOpenChange(false);

  if (check.isLoading || check.error || !check.data) {
    return (
      <ConfirmDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Delete project?"
        description={
          check.error
            ? resolveApiErrorMessage(
                check.error,
                'Could not check whether this project can be deleted.',
              )
            : 'Checking whether this project can be deleted...'
        }
        confirmLabel={check.error ? 'Retry' : 'Delete'}
        confirmVariant="destructive"
        isPending={check.isLoading}
        pendingLabel="Checking..."
        onConfirm={() => void check.refetch()}
      />
    );
  }

  const { canDelete, submissionCount, categoryCount, priceCount } = check.data;

  if (!canDelete) {
    return (
      <ConfirmDialog
        open={open}
        onOpenChange={onOpenChange}
        title="This project cannot be deleted"
        description={`${project.displayName} has ${formatCount(submissionCount, 'submission')}. ${
          project.isActive
            ? 'Deactivate it instead to stop new submissions.'
            : 'It is already inactive, so no new submissions can be made.'
        }`}
        confirmLabel={project.isActive ? 'Deactivate instead' : 'OK'}
        isPending={isDeactivating}
        pendingLabel="Deactivating..."
        onConfirm={() => {
          if (!project.isActive) return close();
          setStatus(
            { id: project.id, displayName: project.displayName, isActive: false },
            { onSuccess: close },
          );
        }}
      />
    );
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete project?"
      description={`${project.displayName} and its price grid (${formatCount(
        categoryCount,
        'category',
      )}, ${formatCount(priceCount, 'price')}) will be removed.`}
      confirmLabel="Delete"
      confirmVariant="destructive"
      isPending={isDeleting}
      pendingLabel="Deleting..."
      onConfirm={() => deleteProject(project.id)}
    />
  );
}
